import {useCallback, useContext, useState} from "react";
import {observer} from "mobx-react";
import {Field} from "@headlessui/react";
import {type SubmitHandler, useForm, Controller} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {
  DropdownSelect,
  PhoneInput,
  Label,
  type TSelectItem,
  Checkbox,
  Button,
  FieldError,
  YStack,
} from "@chayka/ui";
import {Already} from "./Already";
import {SignUpStateContext, SignUpSecondSchema, type SignUpSecondData} from "./state";
import {trpc} from "@/app/trpc";
import {showErrorToast, showSuccessToast} from "@/utils/toast";
import {BottomSheet, Rules} from "@/components";

export type UserType =
  | "Музыкальная группа"
  | "Преподаватель по вокалу"
  | "Преподаватель по гитаре"
  | "Преподаватель по ударным"
  | "Индивидуальный барабанщик"
  | "Индивидуальный вокалист"
  | "Музыкальная школа"
  | "Сотрудник студии Чайка"
  | "Звукорежиссёр"
  | "Саксофонист";

export const USER_TYPE_ITEMS: Array<TSelectItem<UserType>> = [
  {label: "Музыкальная группа", value: "Музыкальная группа"},
  {label: "Преподаватель по вокалу", value: "Преподаватель по вокалу"},
  {label: "Преподаватель по гитаре", value: "Преподаватель по гитаре"},
  {label: "Преподаватель по ударным", value: "Преподаватель по ударным"},
  {label: "Индивидуальный барабанщик", value: "Индивидуальный барабанщик"},
  {label: "Индивидуальный вокалист", value: "Индивидуальный вокалист"},
  {label: "Музыкальная школа", value: "Музыкальная школа"},
  {label: "Сотрудник студии Чайка", value: "Сотрудник студии Чайка"},
  {label: "Звукорежиссёр", value: "Звукорежиссёр"},
  {label: "Саксофонист", value: "Саксофонист"},
];

export const SignUpSecondStep = observer(() => {
  const [rulesOpen, setRulesOpen] = useState(false);

  const handleClickRules = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    setRulesOpen(true);
  }, []);

  const queryClient = useQueryClient();
  const DUPLICATE_PHONE_ERROR = "Пользователь с таким номером телефона уже существует";
  const {firstStepData, secondStepData, setSecondStepData} = useContext(SignUpStateContext);

  const signUpMutator = useMutation({
    ...trpc.auth.signUp.mutationOptions(),
    onSuccess: async () => {
      await queryClient.invalidateQueries(trpc.auth.me.queryFilter());
      showSuccessToast("Вы успешно зарегистрировались");
    },
    onError: (error) => {
      if (error.message === DUPLICATE_PHONE_ERROR) {
        setError("phone", {
          type: "manual",
          message: DUPLICATE_PHONE_ERROR,
        });
        return;
      }
      showErrorToast(error.message);
    },
  });

  const {
    control,
    handleSubmit,
    clearErrors,
    setError,
    formState: {errors, isSubmitting},
  } = useForm<SignUpSecondData>({
    resolver: zodResolver(SignUpSecondSchema),
    defaultValues: {
      type: secondStepData.type,
      phone: secondStepData.phone,
      agree: secondStepData.agree,
    },
  });

  const onSubmit: SubmitHandler<SignUpSecondData> = (data) => {
    clearErrors("phone");
    setSecondStepData(data);
    signUpMutator.mutate({...firstStepData, ...data});
  };

  return (
    <>
      <YStack className="justify-between h-full gap-10">
        <YStack className="gap-5">
          <Field className="flex flex-col relative z-10">
            <Label required>Тип пользователя</Label>
            <Controller
              name="type"
              control={control}
              render={({field}) => (
                <DropdownSelect
                  selected={USER_TYPE_ITEMS.find((item) => item.value === field.value) ?? null}
                  items={USER_TYPE_ITEMS}
                  onSelect={(item) => field.onChange(item.value)}
                  placeholder="Выберите тип пользователя"
                />
              )}
            />
            <FieldError error={errors.type?.message} />
          </Field>

          <Field className="flex flex-col relative">
            <Label required>Номер телефона</Label>
            <Controller
              name="phone"
              control={control}
              render={({field}) => (
                <PhoneInput
                  {...field}
                  value={field.value ?? ""}
                  onChange={(event) => {
                    clearErrors("phone");
                    field.onChange(event);
                  }}
                />
              )}
            />
            <FieldError error={errors.phone?.message} />
          </Field>

          <Controller
            name="agree"
            control={control}
            render={({field}) => (
              <YStack className="gap-1">
                <Field className="flex flex-row items-center space-x-2 relative">
                  <Checkbox checked={field.value} onChange={field.onChange} />
                  <Label className="mb-0! font-normal">
                    Я согласен с{" "}
                    <span onClick={handleClickRules} className="text-primary">
                      Правилами Chayka Studia
                    </span>
                  </Label>
                </Field>
                <FieldError error={errors.agree?.message} />
              </YStack>
            )}
          />
        </YStack>

        <YStack className="gap-5">
          <Button
            onClick={handleSubmit(onSubmit)}
            loading={isSubmitting || signUpMutator.isPending}
          >
            Зарегистрироваться
          </Button>
          <Already />
        </YStack>
      </YStack>

      <BottomSheet fullHeight isOpen={rulesOpen} onClose={() => setRulesOpen(false)}>
        <h1 className="font-bold text-xl mb-2 text-center">Правила студии</h1>
        <Rules />
      </BottomSheet>
    </>
  );
});
